import { Logger } from "@moment/logging";

import { type AwsHttpResponse, awsApi } from "../_interop/aws";
import { $selectedAwsProfile, $selectedAwsRegion } from "../aws";
import { store } from "../store";
import { createSafeResponse } from "./safe-response";

const logger = new Logger("aws-fetch-interceptor");

/**
 * Extract the AWS service name from a hostname.
 * Handles both path-style and virtual-hosted-style URLs:
 *   "s3.us-west-2.amazonaws.com"        → "s3"
 *   "dynamodb.us-west-2.amazonaws.com"   → "dynamodb"
 *   "bucket.s3.us-west-2.amazonaws.com"  → "s3"  (virtual-hosted S3)
 */
const extractService = (hostname: string): string => {
	const parts = hostname.split(".");
	for (let i = 0; i < parts.length; i++) {
		if (parts[i] === "s3" || parts[i] === "s3-accesspoint" || parts[i] === "s3-fips") {
			return "s3";
		}
	}
	return parts[0];
};

/**
 * Extract region from an AWS hostname, e.g.:
 *   "sts.us-east-1.amazonaws.com"       → "us-east-1"
 *   "s3.eu-west-1.amazonaws.com"        → "eu-west-1"
 *   "s3.amazonaws.com"                  → null (global endpoint)
 *   "bucket.s3.us-west-2.amazonaws.com" → "us-west-2"
 */
const extractRegion = (hostname: string): string | null => {
	const match = hostname.match(/\.([a-z]{2}-[a-z]+-\d+)\.amazonaws\.com$/);
	return match ? match[1] : null;
};

/**
 * Headers the SDK adds via its signing middleware — Rust will re-sign with real credentials.
 * x-amz-content-sha256 is NOT stripped — it's a content hash required by S3.
 */
const AUTH_HEADERS = new Set(["authorization", "x-amz-date", "x-amz-security-token"]);

let installed = false;

/**
 * Ensure a global fetch interceptor is installed that routes AWS SDK requests
 * (*.amazonaws.com) through the Rust proxy for SigV4 signing.
 *
 * Safe to call multiple times — only installs once. The awsRuntime.ts in
 * packages/app also installs an equivalent interceptor; whichever runs first
 * wins (both use an `installed` guard keyed to their own module scope, but
 * the underlying behavior is identical and idempotent since Rust does the
 * actual signing).
 */
export const ensureAwsFetchInterceptor = (): void => {
	if (installed) {
		return;
	}
	installed = true;

	const api = awsApi();
	const originalFetch = globalThis.fetch.bind(globalThis);

	globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
		let url: string;
		if (typeof input === "string") {
			url = input;
		} else if (input instanceof URL) {
			url = input.toString();
		} else if (input instanceof Request) {
			url = input.url;
		} else {
			return originalFetch(input, init);
		}

		let parsed: URL;
		try {
			parsed = new URL(url);
		} catch {
			return originalFetch(input, init);
		}

		if (!parsed.hostname.endsWith(".amazonaws.com")) {
			return originalFetch(input, init);
		}

		const profile = await store.get($selectedAwsProfile);
		if (!profile) {
			throw new Error("No AWS account/role selected. Sign in via the Environments popover.");
		}

		const service = extractService(parsed.hostname);
		const region =
			extractRegion(parsed.hostname) ?? (await store.get($selectedAwsRegion)) ?? "us-east-1";

		// Collect headers, stripping auth headers the SDK added with dummy credentials
		const headers: Record<string, string> = {};
		const rawHeaders = init?.headers ?? (input instanceof Request ? input.headers : undefined);
		if (rawHeaders) {
			if (rawHeaders instanceof Headers) {
				rawHeaders.forEach((value, key) => {
					if (!AUTH_HEADERS.has(key.toLowerCase())) {
						headers[key] = value;
					}
				});
			} else if (Array.isArray(rawHeaders)) {
				for (const [key, value] of rawHeaders) {
					if (!AUTH_HEADERS.has(key.toLowerCase())) {
						headers[key] = value;
					}
				}
			} else {
				for (const [key, value] of Object.entries(rawHeaders as Record<string, string>)) {
					if (!AUTH_HEADERS.has(key.toLowerCase())) {
						headers[key] = value;
					}
				}
			}
		}

		// Extract body
		let body: string | null = null;
		const rawBody = init?.body ?? (input instanceof Request ? input.body : undefined);
		if (rawBody) {
			if (typeof rawBody === "string") {
				body = rawBody;
			} else if (rawBody instanceof Uint8Array) {
				body = new TextDecoder().decode(rawBody);
			} else if (rawBody instanceof ArrayBuffer) {
				body = new TextDecoder().decode(new Uint8Array(rawBody));
			} else if (rawBody instanceof ReadableStream) {
				body = await new Response(rawBody).text();
			} else if (rawBody instanceof Blob) {
				body = await rawBody.text();
			} else {
				body = String(rawBody);
			}
		}

		const method = init?.method ?? (input instanceof Request ? input.method : "GET");

		let response: AwsHttpResponse;
		try {
			response = await api.executeRequest({
				profile,
				service,
				region,
				method,
				url,
				headers: Object.keys(headers).length > 0 ? headers : null,
				body,
			});
		} catch (err) {
			const msg =
				err instanceof Error
					? err.message
					: typeof err === "string"
						? err
						: JSON.stringify(err);
			throw new Error(`AWS proxy error: ${msg}`);
		}

		let responseBody: BodyInit = response.body;
		if (response.body_base64) {
			const binary = atob(response.body_base64);
			const bytes = new Uint8Array(binary.length);
			for (let i = 0; i < binary.length; i++) {
				bytes[i] = binary.charCodeAt(i);
			}
			responseBody = bytes;
		}

		return createSafeResponse(responseBody, {
			status: response.status,
			statusText: response.status_text,
			headers: new Headers(response.headers),
		});
	};

	logger.debug("AWS fetch interceptor installed");
};
